import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config/env";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Enquiry received. Our engineering desk will respond within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error("Could not submit. Please email us directly at contact@debsuntechnologies.com.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-28 lg:py-36 scroll-mt-[88px]" data-testid="contact">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="sec-index mb-4">07 — Contact</div>
            <h2 className="display display-section text-4xl md:text-6xl">Start an <span style={{ color: "var(--gold)" }}>engagement</span>.</h2>
            <p className="mt-6 text-base md:text-lg text-steel-text leading-relaxed max-w-md">
              Send a problem statement, a drawing or a rough bill of quantities. We reply with a
              first engineering sketch inside one working day.
            </p>

            <div className="mt-12 space-y-6">
              {[
                { icon: MapPin, label: "Registered Office", value: "B-435, Supath II Commercial Complex, Near Old Vadaj Bus Stop, Ashram Road, Usmanpura, Ahmedabad 380013" },
                { icon: Phone, label: "Telephone", value: "+91 99247 92096", href: "tel:+919924792096" },
                { icon: Mail, label: "General Enquiries", value: "contact@debsuntechnologies.com", href: "mailto:contact@debsuntechnologies.com", testId: "contact-email-general" },
                { icon: Mail, label: "Customer Support", value: "support@debsuntechnologies.in", href: "mailto:support@debsuntechnologies.in", testId: "contact-email-support" },
                { icon: Linkedin, label: "LinkedIn", value: "/in/abhilashdebsun", href: "https://linkedin.com/in/abhilashdebsun" },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-border flex items-center justify-center text-[color:var(--gold)] shrink-0"><Icon size={15} /></div>
                    <div>
                      <div className="font-mono-2 text-[10px] tracking-[0.28em] uppercase text-muted-foreground">{c.label}</div>
                      {c.href
                        ? <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={c.testId} className="mt-1 block text-sm md:text-base aurora-underline w-fit">{c.value}</a>
                        : <div className="mt-1 text-sm leading-relaxed max-w-xs">{c.value}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 lg:col-start-7 steel-card clip-frame p-7 sm:p-9 lg:p-11"
            data-testid="contact-form"
            id="enquiry"
          >
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-8">Enquiry Form · REF DBN-WEB</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="contact-name" className="rounded-none h-12" />
              <Input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="contact-email" className="rounded-none h-12" />
              <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="rounded-none h-12" />
              <Input placeholder="Company" value={form.company} onChange={set("company")} data-testid="contact-company" className="rounded-none h-12" />
            </div>
            <Textarea required rows={5} placeholder="Describe your requirement — plant, project, scope, quantities, geography…"
              value={form.message} onChange={set("message")} data-testid="contact-message" className="rounded-none mt-4" />
            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-muted-foreground max-w-xs">By submitting you agree to be contacted regarding your enquiry.</div>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="btn-gold clip-frame-sm px-7 py-4 text-xs uppercase tracking-[0.2em] font-mono-2 inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? "Sending…" : (<>Send Enquiry <Send size={14} /></>)}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
